import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  LISTEN_TYPEN,
  ListenStruktur,
  ListenStrukturVorlage,
  ListenTyp,
  kennzeichnungsSpalte,
  pruefeStruktur
} from '../../../../../Models/messstelle.model';
import { InbetriebnahmeService } from '../../../../../Services/inbetriebnahme.service';
import { AuthService } from '../../../../../api-authorization/authorize.service';


type ErstellArt = 'vorlage' | 'eigene';


/**
 * Leere Inbetriebnahmeliste ohne Excel erstellen.
 *
 * Zwei Wege, ein Ergebnis: entweder eine gespeicherte Vorlage wählen
 * (Vorschau der Spalten, Kennzeichnung sichtbar) oder die Spalten selbst
 * definieren und optional gleich als Vorlage ablegen. Danach öffnet sich die
 * bestehende Listenansicht – zunächst ohne Einträge; Messstellen kommen über
 * „Messstelle hinzufügen“ dazu, genau wie bei importierten Listen.
 */
@Component({
  selector: 'app-liste-erstellen',
  templateUrl: './liste-erstellen.component.html',
  styleUrls: ['./liste-erstellen.component.css']
})
export class ListeErstellenComponent implements OnInit {

  /** Projekt aus dem Aufruf (Projektseite). Ohne Projekt entsteht eine freie Liste. */
  projektId: number | null = null;
  projektName: string | null = null;

  name = '';
  listenTyp: ListenTyp = 'Messstellenliste';

  readonly listenTypen = LISTEN_TYPEN.filter(t => t.wert !== 'Unbekannt');

  art: ErstellArt = 'vorlage';

  // ── Aus Vorlage ──
  vorlagen: ListenStrukturVorlage[] = [];
  vorlagenLaden = true;
  vorlagenFehler = '';
  gewaehlteVorlage: ListenStrukturVorlage | null = null;
  vorlagenSuche = '';

  // ── Eigene Struktur ──
  eigeneStruktur: ListenStruktur = { spalten: [] };
  eigeneGueltig = false;
  alsVorlageSpeichern = false;
  vorlagenName = '';
  vorlagenBeschreibung = '';

  speichert = false;
  fehler = '';


  constructor(
    private service: InbetriebnahmeService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) { }


  get canEdit(): boolean {
    return this.authService.canEdit();
  }


  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const projektId = Number(params.get('projektId'));

    this.projektId = Number.isFinite(projektId) && projektId > 0 ? projektId : null;
    this.projektName = params.get('projektName');

    // Aus der Vorlagenverwaltung: Vorlage gleich vorausgewählt.
    const vorlageId = Number(params.get('vorlageId'));
    this.vorgewaehlteVorlageId = Number.isFinite(vorlageId) && vorlageId > 0 ? vorlageId : null;

    this.ladeVorlagen();
  }


  private vorgewaehlteVorlageId: number | null = null;


  private ladeVorlagen(): void {
    this.vorlagenLaden = true;
    this.vorlagenFehler = '';

    this.service.getStrukturVorlagen().subscribe({
      next: vorlagen => {
        this.vorlagen = vorlagen;
        this.vorlagenLaden = false;

        // Ohne Vorlagen führt der Weg direkt zur eigenen Struktur.
        if (vorlagen.length === 0) {
          this.art = 'eigene';
        }

        const vorgewaehlt = vorlagen.find(v => v.id === this.vorgewaehlteVorlageId);

        if (vorgewaehlt) {
          this.vorlageWaehlen(vorgewaehlt);
        }
      },
      error: () => {
        this.vorlagenLaden = false;
        this.vorlagenFehler = 'Die Vorlagen konnten nicht geladen werden.';
        this.art = 'eigene';
      }
    });
  }


  // ── Vorlagen ──

  get gefilterteVorlagen(): ListenStrukturVorlage[] {
    const suche = this.vorlagenSuche.trim().toLowerCase();

    if (!suche) {
      return this.vorlagen;
    }

    return this.vorlagen.filter(v =>
      v.name.toLowerCase().includes(suche)
      || (v.beschreibung ?? '').toLowerCase().includes(suche)
      || v.struktur.spalten.some(s => s.name.toLowerCase().includes(suche)));
  }


  vorlageWaehlen(vorlage: ListenStrukturVorlage): void {
    this.gewaehlteVorlage = vorlage;
    this.listenTyp = vorlage.listenTyp === 'Unbekannt' ? 'Messstellenliste' : vorlage.listenTyp;
    this.fehler = '';
  }


  kennzeichnungVon(vorlage: ListenStrukturVorlage): string {
    return kennzeichnungsSpalte(vorlage.struktur)?.name ?? vorlage.kennzeichnungLabel ?? 'Kennzeichnung';
  }


  listenTypText(typ: ListenTyp): string {
    return LISTEN_TYPEN.find(t => t.wert === typ)?.text ?? typ;
  }


  trackByVorlage(_index: number, vorlage: ListenStrukturVorlage): number {
    return vorlage.id;
  }


  // ── Eigene Struktur ──

  strukturGeaendert(struktur: ListenStruktur): void {
    this.eigeneStruktur = struktur;
    this.fehler = '';
  }


  gueltigkeitGeaendert(gueltig: boolean): void {
    this.eigeneGueltig = gueltig;
  }


  get eigeneKennzeichnung(): string | null {
    return kennzeichnungsSpalte(this.eigeneStruktur)?.name || null;
  }


  // ── Erstellen ──

  get nameGueltig(): boolean {
    return this.name.trim().length > 0;
  }


  get kannErstellen(): boolean {
    if (this.speichert || !this.nameGueltig || !this.canEdit) {
      return false;
    }

    if (this.art === 'vorlage') {
      return this.gewaehlteVorlage != null;
    }

    if (!this.eigeneGueltig || pruefeStruktur(this.eigeneStruktur).length > 0) {
      return false;
    }

    return !this.alsVorlageSpeichern || this.vorlagenName.trim().length > 0;
  }


  erstellen(): void {

    if (!this.kannErstellen) {
      return;
    }

    this.speichert = true;
    this.fehler = '';

    const request = this.art === 'vorlage'
      ? {
          projektId: this.projektId,
          name: this.name.trim(),
          vorlageId: this.gewaehlteVorlage!.id,
          listenTyp: this.listenTyp
        }
      : {
          projektId: this.projektId,
          name: this.name.trim(),
          listenTyp: this.listenTyp,
          struktur: this.eigeneStruktur,
          alsVorlageSpeichern: this.alsVorlageSpeichern,
          vorlagenName: this.alsVorlageSpeichern ? this.vorlagenName.trim() : null,
          vorlagenBeschreibung: this.alsVorlageSpeichern ? (this.vorlagenBeschreibung.trim() || null) : null
        };

    this.service.createManuelleListe(request).subscribe({
      next: liste => {
        this.speichert = false;

        this.snackBar.open(
          this.art === 'eigene' && this.alsVorlageSpeichern
            ? 'Liste erstellt und Struktur als Vorlage gespeichert.'
            : 'Leere Liste erstellt – jetzt Messstellen hinzufügen.',
          'OK',
          { duration: 4000 }
        );

        this.router.navigate(['/wartung/inbetriebnahme/liste', liste.id]);
      },
      error: err => {
        this.speichert = false;
        this.fehler = err?.error?.message ?? 'Die Liste konnte nicht erstellt werden.';
      }
    });
  }


  zurueck(): void {
    if (this.projektId != null) {
      this.router.navigate(['/projekte', this.projektId]);
    } else {
      this.router.navigate(['/wartung/inbetriebnahme']);
    }
  }


  zuVorlagen(): void {
    this.router.navigate(['/wartung/inbetriebnahme/vorlagen']);
  }
}
